import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import { Bot, Loader2, MessageCircle } from 'lucide-react';

const WhatsAppInboxPage = React.lazy(() => import('@/pages/admin/WhatsAppInboxPage'));

/**
 * The WhatsApp pill in the admin header, and the slide-over it opens.
 *
 * The pill counts unread inbound messages (live) and opens the inbox in a
 * side panel from any admin page — no tab change. The panel's top strip is
 * the assistant's controls: mode (Off / Away only / Always), office hours,
 * and an "I'm away" switch. Owner decision 2026-09-09: Away only from day
 * one, 07:00–17:00, the assistant answers outside those hours unless an
 * admin switches it off.
 */

const VOICE_PROVIDERS = [
    { value: 'auto', label: 'Auto', hint: 'ElevenLabs when its key is set, otherwise OpenAI' },
    { value: 'elevenlabs', label: 'ElevenLabs', hint: 'George — about 9p a voice reply' },
    { value: 'openai', label: 'OpenAI', hint: 'About 1p a voice reply; the voice on the right' },
];
// Accent and gender both set the OpenAI base voice, because the base voice out-votes the
// instructions: a "British" cedar still reads American. OPENAI_BASE mirrors
// OPENAI_ACCENT_VOICE in _shared/whatsappVoice.ts — keep the two in step.
const OPENAI_BASE = { uk: { male: 'fable', female: 'marin' }, us: { male: 'cedar', female: 'marin' } };
const VOICE_ACCENTS = [
    { value: 'uk', label: 'UK', hint: 'British: George or Alice on ElevenLabs, Fable on OpenAI' },
    { value: 'us', label: 'US', hint: 'American: Eric or Bella on ElevenLabs, Cedar on OpenAI' },
];
const VOICE_GENDERS = [
    { value: 'male', label: 'Male', hint: 'George (UK) / Eric (US) on ElevenLabs; Fable / Cedar on OpenAI' },
    { value: 'female', label: 'Female', hint: 'Alice (UK) / Bella (US) on ElevenLabs. OpenAI has no natively British female voice — for a certain British woman use ElevenLabs' },
];
const OPENAI_VOICES = [
    { value: 'fable', label: 'Fable — male, British-leaning' },
    { value: 'ballad', label: 'Ballad — male, British-leaning' },
    { value: 'cedar', label: 'Cedar — male, American-leaning, newest' },
    { value: 'ash', label: 'Ash — male, American-leaning' },
    { value: 'onyx', label: 'Onyx — male, deep' },
    { value: 'echo', label: 'Echo — male' },
    { value: 'verse', label: 'Verse — male' },
    { value: 'marin', label: 'Marin — female, newest' },
    { value: 'sage', label: 'Sage — female' },
    { value: 'coral', label: 'Coral — female, warm' },
    { value: 'shimmer', label: 'Shimmer — female, bright' },
    { value: 'nova', label: 'Nova — female' },
];
const MODES = [
    { value: 'off',    label: 'Off',       hint: 'Never answers.' },
    { value: 'away',   label: 'Away only', hint: 'Answers outside office hours, or when you flag yourself away.' },
    { value: 'always', label: 'Always',    hint: 'Answers every message, day and night.' },
];

const hhmm = (t) => String(t || '').slice(0, 5);

// headless / openRef / onCount let the Smart Dash frame host this desk without
// its header pill: the Island opens it and Needs you counts its unread. With no
// props it renders exactly as before.
export default function WhatsAppHeaderPill({ headless = false, openRef = null, onCount = null } = {}) {
    const { toast } = useToast();
    const [open, setOpen] = useState(false);
    const [unread, setUnread] = useState(0);
    useEffect(() => { if (openRef) openRef.current = () => setOpen(true); }, [openRef]);
    useEffect(() => { onCount?.(unread); }, [onCount, unread]);
    const [settings, setSettings] = useState(null);
    const [gate, setGate] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        const [{ data: n }, { data: s }, { data: g }] = await Promise.all([
            supabase.rpc('whatsapp_unread_count'),
            supabase.from('whatsapp_rescue_settings')
                .select('*').maybeSingle(),
            supabase.rpc('whatsapp_bot_should_answer', { p_thread_key: '__status__' }),
        ]);
        if (typeof n === 'number') setUnread(n);
        if (s) setSettings(s);
        if (g) setGate(g);
    }, []);

    useEffect(() => { load(); }, [load]);

    // Live: any message in or out, or a settings change, refreshes the pill.
    useEffect(() => {
        const ch = supabase.channel('whatsapp-header')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'whatsapp_messages' }, () => load())
            .on('postgres_changes', { event: '*', schema: 'public', table: 'whatsapp_rescue_settings' }, () => load())
            .subscribe();
        const t = setInterval(load, 60000); // the office-hours boundary passes without any row changing
        return () => { clearInterval(t); supabase.removeChannel(ch).catch(() => {}); };
    }, [load]);

    const save = async (patch) => {
        setSaving(true);
        try {
            const { error } = await supabase.from('whatsapp_rescue_settings')
                .update({ ...patch, updated_at: new Date().toISOString() }).eq('id', true);
            if (error) throw error;
            await load();
        } catch (e) {
            toast({ title: 'Could not save', description: e.message, variant: 'destructive' });
        } finally {
            setSaving(false);
        }
    };

    const answering = !!gate?.answer;
    const mode = settings?.bot_mode || 'away';
    const voiceProvider = settings?.voice_provider || 'auto';
    const voiceAccent = settings?.voice_accent || 'uk';
    const voiceGender = settings?.voice_gender || 'male';
    const [welcomeDraft, setWelcomeDraft] = useState(null);   // null = not editing
    const [sidDraft, setSidDraft] = useState(null);

    return (
        <>
            {!headless && <button
                type="button"
                onClick={() => setOpen(true)}
                title={unread ? `${unread} unread WhatsApp message${unread === 1 ? '' : 's'}` : 'WhatsApp inbox'}
                className={`flex items-center gap-1.5 px-3 h-7 rounded-lg border text-[11px] font-semibold transition-colors ${
                    unread
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800/50 dark:bg-emerald-900/20 dark:text-emerald-300'
                        : 'border-border bg-card text-muted-foreground hover:bg-accent/40'}`}
            >
                <MessageCircle className="h-3 w-3" />
                <span className="hidden lg:inline">WhatsApp</span>
                {unread > 0 && (
                    <span className="ml-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-emerald-600 px-1 text-[9px] font-bold text-white">{unread}</span>
                )}
                {answering && <span title="Assistant is answering right now" className="ml-0.5 h-1.5 w-1.5 rounded-full bg-sky-500" />}
            </button>}

            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent side="right" className="w-full overflow-y-auto p-0 sm:max-w-5xl">
                    <SheetHeader className="border-b border-border px-5 py-4">
                        <SheetTitle className="flex items-center gap-2 text-base"><MessageCircle size={18} className="text-emerald-600" /> WhatsApp</SheetTitle>
                        <SheetDescription className="text-xs">Replies from applicants and your answers. Reply freely for 24 hours after their last message.</SheetDescription>

                        {/* Assistant controls */}
                        <div className="mt-3 rounded-2xl border border-border bg-muted/30 p-3">
                            <div className="flex flex-wrap items-center gap-3">
                                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${answering ? 'bg-sky-100 text-sky-700' : 'bg-slate-200 text-slate-600'}`}>
                                    <Bot size={12} /> {answering ? 'Assistant answering' : 'Assistant asleep — you\'re on'}
                                </span>
                                <span className="text-[11px] text-muted-foreground">
                                    {gate?.local_time ? `${gate.local_time} ${settings?.bot_timezone || ''}` : ''}
                                    {gate?.reason ? ` · ${String(gate.reason).replaceAll('_', ' ')}` : ''}
                                </span>
                                {saving && <Loader2 size={12} className="animate-spin text-muted-foreground" />}
                            </div>
                            <div className="mt-3 grid gap-3 sm:grid-cols-[auto_1fr] sm:items-center">
                                <div className="flex gap-1">
                                    {MODES.map((m) => (
                                        <button key={m.value} type="button" disabled={saving} title={m.hint}
                                            onClick={() => save({ bot_mode: m.value })}
                                            className={`rounded-lg border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${mode === m.value ? 'border-sky-300 bg-sky-50 text-sky-700' : 'border-border bg-card text-muted-foreground hover:bg-accent/40'}`}>
                                            {m.label}
                                        </button>
                                    ))}
                                </div>
                                <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
                                    <label className="flex items-center gap-1.5">
                                        Office hours
                                        <input type="time" value={hhmm(settings?.bot_office_start) || '07:00'} disabled={saving}
                                            onChange={(e) => e.target.value && save({ bot_office_start: e.target.value })}
                                            className="h-7 rounded-md border border-border bg-card px-1.5 text-[11px]" />
                                        –
                                        <input type="time" value={hhmm(settings?.bot_office_end) || '17:00'} disabled={saving}
                                            onChange={(e) => e.target.value && save({ bot_office_end: e.target.value })}
                                            className="h-7 rounded-md border border-border bg-card px-1.5 text-[11px]" />
                                        <span>Mon–Fri</span>
                                    </label>
                                    <label className="flex items-center gap-2">
                                        <Switch checked={!!settings?.bot_away_now} disabled={saving || mode === 'off'}
                                            onCheckedChange={(v) => save({ bot_away_now: v })} />
                                        I'm away <span className="text-muted-foreground/70">(assistant answers now)</span>
                                    </label>
                                </div>
                            </div>
                            <div className="mt-3 grid gap-2 border-t border-border/60 pt-3 sm:grid-cols-2">
                                <label className="flex items-center gap-2 text-[11px] text-muted-foreground">
                                    <Switch checked={settings?.welcome_enabled !== false} disabled={saving} onCheckedChange={(v) => save({ welcome_enabled: v })} />
                                    Welcome first-time chats <span className="text-muted-foreground/70">(a greeting gets the ask / book-a-call choices)</span>
                                </label>
                                <label className="flex items-center gap-2 text-[11px] text-muted-foreground">
                                    <Switch checked={settings?.voice_replies_enabled !== false} disabled={saving} onCheckedChange={(v) => save({ voice_replies_enabled: v })} />
                                    Reply to voice notes with voice <span className="text-muted-foreground/70">(WhatsApp only)</span>
                                </label>
                                {/* Owner rule (2026-09-10): which voice speaks — compare the two by ear. */}
                                <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
                                    <span className="text-[11px] text-muted-foreground">Voice:</span>
                                    <div className="flex gap-1">
                                        {VOICE_PROVIDERS.map((p) => (
                                            <button key={p.value} type="button" disabled={saving || settings?.voice_replies_enabled === false} title={p.hint}
                                                onClick={() => save({ voice_provider: p.value })}
                                                className={`rounded-lg border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${voiceProvider === p.value ? 'border-sky-300 bg-sky-50 text-sky-700' : 'border-border bg-card text-muted-foreground hover:bg-accent/40'}`}>
                                                {p.label}
                                            </button>
                                        ))}
                                    </div>
                                    <span className="text-[11px] text-muted-foreground">Voice is:</span>
                                    <div className="flex gap-1">
                                        {VOICE_GENDERS.map((g) => (
                                            <button key={g.value} type="button" disabled={saving || settings?.voice_replies_enabled === false} title={g.hint}
                                                onClick={() => save({ voice_gender: g.value, voice_openai_voice: OPENAI_BASE[voiceAccent][g.value] })}
                                                className={`rounded-lg border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${voiceGender === g.value ? 'border-sky-300 bg-sky-50 text-sky-700' : 'border-border bg-card text-muted-foreground hover:bg-accent/40'}`}>
                                                {g.label}
                                            </button>
                                        ))}
                                    </div>
                                    <span className="text-[11px] text-muted-foreground">Accent:</span>
                                    <div className="flex gap-1">
                                        {VOICE_ACCENTS.map((a) => (
                                            <button key={a.value} type="button" disabled={saving || settings?.voice_replies_enabled === false} title={a.hint}
                                                onClick={() => save({ voice_accent: a.value, voice_openai_voice: OPENAI_BASE[a.value][voiceGender] })}
                                                className={`rounded-lg border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${voiceAccent === a.value ? 'border-sky-300 bg-sky-50 text-sky-700' : 'border-border bg-card text-muted-foreground hover:bg-accent/40'}`}>
                                                {a.label}
                                            </button>
                                        ))}
                                    </div>
                                    <select
                                        value={settings?.voice_openai_voice || 'fable'}
                                        disabled={saving || settings?.voice_replies_enabled === false}
                                        onChange={(e) => save({ voice_openai_voice: e.target.value })}
                                        title="The OpenAI voice, used whenever OpenAI speaks"
                                        className="rounded-md border border-border bg-card px-2 py-1 text-[11px]"
                                    >
                                        {OPENAI_VOICES.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                                    </select>
                                    <span className="text-[10px] text-muted-foreground/70">
                                        ElevenLabs ≈ 9p a voice reply · OpenAI ≈ 0.5p. Send the number a voice note to hear the one selected.
                                        {voiceGender === 'female' && voiceAccent === 'uk' && voiceProvider !== 'elevenlabs'
                                            ? ' OpenAI has no natively British female voice, so this one is steered by instruction — switch to ElevenLabs (Alice) if it drifts American.'
                                            : ''}
                                    </span>
                                </div>
                                <div className="sm:col-span-2">
                                    <textarea
                                        rows={2}
                                        value={welcomeDraft ?? settings?.welcome_text ?? ''}
                                        onChange={(e) => setWelcomeDraft(e.target.value)}
                                        onBlur={() => { if (welcomeDraft !== null && welcomeDraft !== settings?.welcome_text) save({ welcome_text: welcomeDraft }); setWelcomeDraft(null); }}
                                        disabled={saving}
                                        placeholder="Welcome text for a first-time chat"
                                        className="w-full rounded-md border border-border bg-card px-2 py-1.5 text-[11px]"
                                    />
                                    <input
                                        value={sidDraft ?? settings?.welcome_content_sid ?? ''}
                                        onChange={(e) => setSidDraft(e.target.value)}
                                        onBlur={() => { if (sidDraft !== null && sidDraft !== (settings?.welcome_content_sid || '')) save({ welcome_content_sid: sidDraft.trim() || null }); setSidDraft(null); }}
                                        disabled={saving}
                                        placeholder="Optional: Twilio quick-reply Content SID (HX…) for WhatsApp buttons — no approval needed in session"
                                        className="mt-1 w-full rounded-md border border-border bg-card px-2 py-1 font-mono text-[10px]"
                                    />
                                </div>
                            </div>
                            <p className="mt-2 text-[10px] text-muted-foreground">
                                The assistant answers from the FAQ, the job listings (outbases, regions, the published pay rules, requirements — all from Admin → Marketing, live), distances when someone gives a postcode, and the applicant's own stage and next step. It can send job, apply and pay-details links, and a fresh continue link when they ask how to carry on. It never invents anything: what it can only half answer it hands to the team in one sentence, and what it can't answer at all gets "a member of the team will get back to you in office hours" once a day per person. Either way the message stays in this inbox for you.
                                Replying to someone yourself pauses it on that thread for 24 hours.
                            </p>
                        </div>
                    </SheetHeader>
                    <div className="p-4 sm:p-5">
                        <Suspense fallback={<div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-400" /></div>}>
                            {open && <WhatsAppInboxPage embedded />}
                        </Suspense>
                    </div>
                </SheetContent>
            </Sheet>
        </>
    );
}
